import is from '@sindresorhus/is';
import { InlineMessage, Message } from '@sr/types';
import type { Store } from 'store';
import useStore from 'store';

export function getActiveMessage(state: Store): Message | undefined {
  const { activeMessageId } = state;
  if (!activeMessageId) return;
  return state.messages.get(activeMessageId);
}

export function useActiveMessage() {
  return useStore(getActiveMessage);
}

// Gets the active message, only if the user has access to it based on
// their pro status. Some messages are available to free users for upselling.
// Returning false is used to indicate that access was blocked for a free user.
export function getActiveMessageGatedByPro(
  state: Store
): Message | undefined | false {
  const { activeMessageId, isPro } = state;
  if (!activeMessageId) return;
  const message = state.messages.get(activeMessageId);
  // Don't show pro messages to free users
  if (!isPro && message && !message.ruleInfo.showToFreeUsers) return false;
  return message;
}

export function useActiveMessageGatedByPro() {
  return useStore(getActiveMessageGatedByPro);
}

export function hasActiveBlockLevelError(srId: string) {
  return function (state: Store): boolean {
    const msg = getActiveMessageGatedByPro(state);
    return is.object(msg) && msg.displayType === 'block' && msg.srId === srId;
  };
}

export function useHasActiveBlockError(srId: string) {
  return useStore(hasActiveBlockLevelError(srId));
}

export function getInlineMessageGatedByPro(id: string) {
  return function (state: Store): InlineMessage | null {
    const message = state.messages.get(id);
    if (message && message.displayType === 'inline') {
      // Don't show pro only messages to free users
      if (!message.ruleInfo.showToFreeUsers && !state.isPro) {
        return null;
      } else {
        return message;
      }
    } else {
      return null;
    }
  };
}

export function useInlineMessageGatedByPro(id: string) {
  return useStore(getInlineMessageGatedByPro(id));
}
